import { useCallback } from 'react'
import { omitKeys } from 'shared/utils/types/omitKeys'
import { useSiteState } from 'siteState/shared'
import { REDIRECT_CALLBACK_KEY } from '../useEditValues'

export type Callbacks = {
  onSucceed?: () => void
  onCancel?: () => void
}

export default function useCancelModal() {
  const [modalCallbacks, setModalCallbacks] = useSiteState<
    Record<string, Callbacks>
  >('cancel-modal-callbacks')
  const [isOpen, setIsOpen] = useSiteState<boolean>('is-cancel-modal-open')
  const [contextId, setContextId] = useSiteState<string>(
    'cancel-modal-context-id'
  )

  const open = useCallback(
    (id?: string) => {
      id && setContextId(id)
      setIsOpen(true)
    },
    [setIsOpen, setContextId]
  )

  const onSuccess = useCallback(() => {
    if (modalCallbacks[contextId]) {
      modalCallbacks[contextId].onSucceed?.()

      setContextId('')
      setModalCallbacks((prevCallbacks) =>
        omitKeys(prevCallbacks, contextId, REDIRECT_CALLBACK_KEY)
      )
    } else {
      Object.keys(modalCallbacks).forEach((key) =>
        modalCallbacks[key].onSucceed?.()
      )

      setModalCallbacks({})
    }

    setIsOpen(false)
  }, [modalCallbacks, setIsOpen, contextId, setContextId, setModalCallbacks])

  const onCancel = useCallback(() => {
    if (modalCallbacks[contextId]) {
      modalCallbacks[contextId].onCancel?.()
      setContextId('')
    } else {
      Object.keys(modalCallbacks).forEach((key) =>
        modalCallbacks[key].onCancel?.()
      )
    }

    setIsOpen(false)
  }, [modalCallbacks, setIsOpen, contextId, setContextId])

  return {
    open,
    isOpen,
    onSuccess,
    onCancel
  }
}
